/**
 * WebSocket realtime client for Django Channels.
 * WebSocket realtime subscriptions for live data updates.
 */

import { API_URL, authStore } from './client'

type SessionCallback = (action: string, record: Record<string, unknown>, type?: string) => void

let ws: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let heartbeatTimer: ReturnType<typeof setInterval> | null = null
let reconnectAttempts = 0
let manualClose = false
const MAX_RECONNECT_ATTEMPTS = 5
const HEARTBEAT_INTERVAL_MS = 30000
const callbacks = new Set<SessionCallback>()

function getRuntimeEnv(key: string): string | undefined {
  if (typeof window !== 'undefined') {
    const win = window as unknown as Record<string, unknown>
    const env = win.__ENV__ as Record<string, string> | undefined
    if (env?.[key] && !env[key].includes('${')) {return env[key]}
  }
  return undefined
}

function enforceWss(url: string): string {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.startsWith('ws:')) {
    return url.replace(/^ws:/, 'wss:')
  }
  return url
}

function resolveWsUrl(): string {
  let base: string
  const runtimeWsUrl = getRuntimeEnv('VITE_WS_URL')
  if (runtimeWsUrl) {
    // VITE_WS_URL is the base path (e.g. wss://api.ubsafety.ca/ws)
    // Each feature appends its own channel subpath
    base = enforceWss(runtimeWsUrl.replace(/\/$/, '') + '/sessions/')
  } else {
    const apiUrl = API_URL.replace(/\/$/, '')
    // Replace http with ws, https with wss
    const wsBase = apiUrl.replace(/^http/, 'ws')
    base = enforceWss(`${wsBase.replace(/\/api\/v1$/, '')}/ws/sessions/`)
  }
  // The backend authenticates WebSockets via a JWT ?token= query param.
  // Read the token fresh on every (re)connect so refreshed tokens are used.
  const token = authStore.token
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

function startHeartbeat() {
  if (heartbeatTimer) {clearInterval(heartbeatTimer)}
  heartbeatTimer = setInterval(() => {
    if (ws?.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: 'ping' }))
      } catch {
        // Connection likely dead; onclose will handle reconnection
      }
    }
  }, HEARTBEAT_INTERVAL_MS)
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

function connect() {
  if (ws?.readyState === WebSocket.OPEN || ws?.readyState === WebSocket.CONNECTING) {
    return
  }

  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.error('[WS] Realtime updates disabled after 5 failed reconnects; refresh the page.')
    return
  }

  if (!authStore.token) {
    // No JWT available (logged out) — the server would reject with 4401 anyway.
    console.warn('[WS] No auth token available. Realtime updates disabled.')
    return
  }

  const url = resolveWsUrl()
  ws = new WebSocket(url)

  ws.onopen = () => {
    console.log('[WS] Connected to sessions channel')
    reconnectAttempts = 0
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    startHeartbeat()
  }

  ws.onmessage = (event) => {
    try {
      const payload = JSON.parse(event.data)
      // Handle heartbeat pong from server (if implemented)
      if (payload.type === 'pong') {return}
      const { action, record, type } = payload
      callbacks.forEach((cb) => cb(action, record, type))
    } catch (err) {
      console.error('[WS] Failed to parse message:', err)
    }
  }

  ws.onclose = () => {
    ws = null
    stopHeartbeat()
    if (manualClose) {
      manualClose = false
      reconnectAttempts = 0
      return
    }
    reconnectAttempts++
    if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
      const delay = Math.min(3000 * reconnectAttempts, 15000)
      console.log(`[WS] Disconnected, reconnecting in ${delay / 1000}s... (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`)
      reconnectTimer = setTimeout(connect, delay)
    } else {
      console.error('[WS] Realtime updates disabled after 5 failed reconnects; refresh the page.')
    }
  }

  ws.onerror = (err) => {
    // Silent on first attempts — onclose handles reconnection
    if (reconnectAttempts === 0) {
      console.error('[WS] Connection error:', err)
    }
    ws?.close()
  }
}

function disconnect() {
  stopHeartbeat()
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  reconnectAttempts = 0
  if (ws) {
    manualClose = true
    ws.close()
    ws = null
  }
}

export function subscribeSessions(
  _topic: string,
  callback: SessionCallback
): () => void {
  callbacks.add(callback)
  connect()

  return () => {
    callbacks.delete(callback)
    if (callbacks.size === 0) {
      disconnect()
    }
  }
}

export function unsubscribeSessions(): void {
  // All subscribers share one connection; individual cleanup is done via returned unsub func
}
